import React, { useState, useEffect, useCallback } from 'react';
import {
  Cog6ToothIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../../contexts/AuthContext';
import { API_URL } from '../../../api/config';

interface CommissionRule {
  id: number;
  name: string;
  percentage: number;
  min_amount: number;
  max_amount: number;
  applies_to: string;
  is_active: boolean;
}

interface MarketplaceSetting {
  key: string;
  value: any;
  description: string;
}

const MarketplaceSettingsPage: React.FC = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState<MarketplaceSetting[]>([]);
  const [commissionRules, setCommissionRules] = useState<CommissionRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'error' | null>(null);
  const [saveMessage, setSaveMessage] = useState('');

  const headers = React.useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [settingsRes, commissionRes] = await Promise.all([
        fetch(`${API_URL}/api/super-admin/marketplace/settings`, { headers }),
        fetch(`${API_URL}/api/marketplace/b2b/commission-rules`, { headers }),
      ]);

      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        const raw = settingsData.settings || settingsData.data;
        if (Array.isArray(raw)) {
          setSettings(raw);
        } else if (raw && typeof raw === 'object') {
          setSettings(Object.entries(raw).map(([key, value]) => ({ key, value, description: '' })));
        } else {
          setSettings([]);
        }
      }

      if (commissionRes.ok) {
        const commissionData = await commissionRes.json();
        const raw = commissionData.rules || commissionData.commissionRules || commissionData.data;
        setCommissionRules(Array.isArray(raw) ? raw : []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveSetting = async (key: string, value: any) => {
    try {
      setSaving(true);
      setSaveStatus(null);
      const res = await fetch(`${API_URL}/api/super-admin/marketplace/settings`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ key, value }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || data.message || 'Failed to save setting');
      }
      setSaveStatus('saved');
      setSaveMessage(`Setting "${key}" saved.`);
      fetchData();
    } catch (err: any) {
      setSaveStatus('error');
      setSaveMessage(err.message || `Failed to save setting "${key}".`);
    } finally {
      setSaving(false);
      setTimeout(() => setSaveStatus(null), 4000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-emerald-200 rounded-full"></div>
          <div className="w-12 h-12 border-4 border-emerald-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-gray-700 via-gray-800 to-gray-900 rounded-2xl p-8 text-white">
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <Cog6ToothIcon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">Marketplace Settings</h1>
              <p className="text-gray-300 mt-1">Configure platform marketplace rules and commission</p>
            </div>
          </div>
          <button onClick={fetchData} className="flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-sm text-white rounded-xl hover:bg-white/20 font-medium transition-all">
            <ArrowPathIcon className="w-5 h-5" />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-center gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500 shrink-0" />
          <p className="text-red-700 text-sm font-medium">{error}</p>
        </div>
      )}

      {/* Save feedback (replaces console-only failures) */}
      {saveStatus && (
        <div role={saveStatus === 'error' ? 'alert' : 'status'} className={`rounded-2xl p-4 flex items-center gap-3 border ${saveStatus === 'error' ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
          {saveStatus === 'error'
            ? <ExclamationTriangleIcon className="w-5 h-5 text-red-500 shrink-0" />
            : <CheckCircleIcon className="w-5 h-5 text-emerald-500 shrink-0" />}
          <p className={`text-sm font-medium ${saveStatus === 'error' ? 'text-red-700' : 'text-emerald-700'}`}>{saveMessage}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-900">General Settings</h3>
          </div>
          <div className="p-6 space-y-4">
            {settings.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No settings configured</p>
            ) : (
              settings.map((setting) => (
                <div key={setting.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-700">{setting.key}</p>
                    {setting.description && <p className="text-xs text-gray-400 mt-0.5">{setting.description}</p>}
                  </div>
                  <div className="ml-4">
                    {typeof setting.value === 'boolean' ? (
                      <button
                        onClick={() => handleSaveSetting(setting.key, !setting.value)}
                        className={`relative w-12 h-6 rounded-full transition-colors ${setting.value ? 'bg-emerald-500' : 'bg-gray-300'}`}
                      >
                        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${setting.value ? 'left-6' : 'left-0.5'}`} />
                      </button>
                    ) : (
                      <input
                        type="text"
                        defaultValue={setting.value || ''}
                        onBlur={(e) => handleSaveSetting(setting.key, e.target.value)}
                        className="w-32 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-right focus:border-emerald-500 focus:ring-0 outline-none"
                      />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Commission Rules */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-bold text-gray-900">Commission Rules</h3>
          </div>
          <div className="p-6">
            {commissionRules.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No commission rules configured</p>
            ) : (
              <div className="space-y-3">
                {commissionRules.map((rule) => (
                  <div key={rule.id} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-sm text-gray-900">{rule.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{rule.applies_to || 'All orders'}</p>
                      </div>
                      <span className="text-lg font-extrabold text-emerald-600">{rule.percentage}%</span>
                    </div>
                    {(rule.min_amount || rule.max_amount) && (
                      <p className="text-xs text-gray-400 mt-2">
                        {rule.min_amount ? `Min: ${rule.min_amount.toLocaleString()}` : ''}
                        {rule.min_amount && rule.max_amount ? ' — ' : ''}
                        {rule.max_amount ? `Max: ${rule.max_amount.toLocaleString()}` : ''}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {saving && (
        <div className="fixed bottom-4 right-4 bg-emerald-600 text-white px-4 py-2 rounded-xl shadow-lg flex items-center gap-2 text-sm font-medium">
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          Saving...
        </div>
      )}
    </div>
  );
};

export default MarketplaceSettingsPage;
